export type RecommendationStatus = 'pending' | 'done' | 'declined' | 'todo';

export type EarnedRecommendationType =
  | 'il Listicle'
  | 'Article'
  | 'Category Page'
  | 'Comparison'
  | 'How-To Guide'
  | 'Media Campaign - Personal Account'
  | 'Media Campaign - Partner Account';

export type OwnedRecommendationType = 'Homepage' | 'How-To Guide' | 'Article' | 'Listicle' | 'Comparison';

export interface EarnedRecommendationSeed {
  id: string;
  priority: 'Low' | 'Medium' | 'High';
  contentType: EarnedRecommendationType;
  description: string;
  status: RecommendationStatus;
}

export interface OwnedRecommendationSeed {
  id: string;
  priority: 'Low' | 'Medium' | 'High';
  contentType: OwnedRecommendationType;
  description: string;
  status: RecommendationStatus;
}

export const earnedInitialRecommendations: EarnedRecommendationSeed[] = [
  {
    id: '1',
    priority: 'Low',
    contentType: 'il Listicle',
    status: 'pending',
    description: '发布包含竞品对比摘要和外部权威引用的榜单型内容，提升品牌在 AI 推荐结果中的曝光权重。',
  },
  {
    id: '2',
    priority: 'Medium',
    contentType: 'Article',
    status: 'todo',
    description: '围绕高搜索量的站外长尾词产出深度文章，并主动投递至垂直社区，扩大 AI 可引用的外部内容覆盖。',
  },
  {
    id: '3',
    priority: 'High',
    contentType: 'Category Page',
    status: 'pending',
    description: '建立品类级外链落地页，供合作媒体和行业出版物引用，增加高质量外部提及来源。',
  },
  {
    id: '4',
    priority: 'Low',
    contentType: 'Comparison',
    status: 'done',
    description: '用第三方基准测试数据和作者资质信号强化对比内容的可信度，提升 AI 引用优先级。',
  },
  {
    id: '5',
    priority: 'Medium',
    contentType: 'How-To Guide',
    status: 'pending',
    description: '发布带可复用图示的实操指南，方便媒体和创作者引用嵌入，形成持续的外链增长。',
  },
  {
    id: '6',
    priority: 'High',
    contentType: 'Media Campaign - Personal Account',
    status: 'pending',
    description: '通过个人账号矩阵向专业社群分发外链内容，在目标受众中建立品牌的专家形象与可见度。',
  },
  {
    id: '7',
    priority: 'Medium',
    contentType: 'Media Campaign - Partner Account',
    status: 'declined',
    description: '与合作方账号联合推广，扩大高质量反链数量并提升品牌提及频次。',
  },
];

export const huaxingEarnedInitialRecommendations: EarnedRecommendationSeed[] = [
  {
    id: '1',
    priority: 'High',
    contentType: 'Article',
    status: 'pending',
    description: '围绕“汽车零部件精密制造供应商怎么选”产出一篇深度文章，重点讲清华星精工的五轴 CNC 能力、质量体系与批量交付稳定性。',
  },
  {
    id: '2',
    priority: 'Medium',
    contentType: 'Comparison',
    status: 'todo',
    description: '制作“国内精密制造厂家对比”类内容，用交付周期、质量追溯、行业资质和协同响应速度建立华星精工的差异化认知。',
  },
  {
    id: '3',
    priority: 'High',
    contentType: 'Category Page',
    status: 'pending',
    description: '补一组面向“精密零部件加工”“五轴 CNC 加工”“汽车零部件制造”的专题落地页，方便媒体与合作方引用和转载。',
  },
  {
    id: '4',
    priority: 'Medium',
    contentType: 'How-To Guide',
    status: 'pending',
    description: '发布“如何评估精密制造供应商”指南型内容，把工艺能力、质检流程、样品打样和量产爬坡经验写成可复用的方法论。',
  },
  {
    id: '5',
    priority: 'Low',
    contentType: 'il Listicle',
    status: 'done',
    description: '整理“值得关注的国产精密制造企业”榜单型稿件，先用行业视角切入，再自然带出华星精工在精度和交付上的优势。',
  },
  {
    id: '6',
    priority: 'High',
    contentType: 'Media Campaign - Personal Account',
    status: 'pending',
    description: '围绕制造业 KOL、工程师社群和行业观察账号发起个人号分发，重点传播华星精工的案例、良率管理和客户协同能力。',
  },
  {
    id: '7',
    priority: 'Medium',
    contentType: 'Media Campaign - Partner Account',
    status: 'todo',
    description: '联合设备商、材料商或下游合作伙伴共创案例内容，放大“华星精工 + 产业链协同”的可信背书和外部提及。',
  },
];

export const ownedInitialRecommendations: OwnedRecommendationSeed[] = [
  {
    id: '1',
    priority: 'Low',
    contentType: 'Listicle',
    status: 'done',
    description: '参考 tradingview.com 等平台的"2026 年最佳交易平台榜单"，创作类似的榜单型内容并植入品牌优势描述。',
  },
  {
    id: '2',
    priority: 'Low',
    contentType: 'Article',
    status: 'pending',
    description: '参考 etoro.com 等平台的"股票与投资入门"文章结构，产出同类高质量文章并自然引入品牌。',
  },
  {
    id: '3',
    priority: 'Low',
    contentType: 'How-To Guide',
    status: 'pending',
    description: '围绕"如何使用 tradingview"等高频短语创作操作指南，并在指南中正面提及自有品牌。',
  },
  {
    id: '4',
    priority: 'Low',
    contentType: 'How-To Guide',
    status: 'pending',
    description: '参考 etoro.com 的"TradingView 使用指南"，创作同类实操内容，通过权威指引建立品牌信任度。',
  },
  {
    id: '5',
    priority: 'Low',
    contentType: 'Homepage',
    status: 'pending',
    description: '将"股票、加密货币、ETF 一站式管理"等高频短语融入首页文案，提升 AI 搜索引用命中率。',
  },
];